const {KitModule, registerModule} = window;

// no space as it will be triggered from click
const OPEN_SUBNAV_KEYS = ['Enter', 'ArrowDown'];

// Switch to the light header after leaving the top of the page.
const LINE_REVEAL_SCROLL = 16;

class Nav extends KitModule {
    constructor($element) {
        super($element);

        this.setVars();
        this.bindEvents();
    }

    setVars() {
        this.searchIsOpen = false;
        this.isMenuOpen = false;
        this.isClicking = false;
        this.clickOpenedSubnav = null; // Track which subnav was opened by click
        this.navHeight = this.$menu.offsetHeight;
        this.isScrolled = null; // null so the first onScroll always applies
        this.$hero = document.querySelector('[data-nav-hero]');
    }

    bindEvents() {
        // events
        this.$element.addEventListener('keydown', this);
        this.$element.addEventListener('click', this);
        this.$element.addEventListener('mouseover', this);
        this.$element.addEventListener('mouseleave', this);
        this.$menuLabel.addEventListener(
            'click',
            this.onMenuLabelClick.bind(this),
        );

        // Add click-outside handler for click-opened subnavs
        document.addEventListener('click', this.onDocumentClick.bind(this));
        // browser hack - fire click on touchstart, avoid :hover & focus
        this.$menuLabel.addEventListener('touchstart', function() {});

        window.addEventListener('searchopen', this);
        window.addEventListener('searchclose', this);

        // Reveal the nav's bottom hairline once scrolled past the top
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, {passive: true});
        window.addEventListener('resize', this.onScroll, {passive: true});
        this.onScroll();

        this.bindMediaQuery();
    }

    // Drive scroll-dependent nav states (see _nav.scss). Only touches the DOM
    // when a state actually flips.
    // - nav--scrolled: light surface after the article hero, or 16px elsewhere
    onScroll() {
        const y = window.scrollY;

        const scrolled = this.$hero ?
            this.$hero.getBoundingClientRect().bottom <= this.$element.getBoundingClientRect().bottom :
            y > LINE_REVEAL_SCROLL;
        if (scrolled !== this.isScrolled) {
            this.isScrolled = scrolled;
            this.$element.classList.toggle('nav--scrolled', scrolled);
        }
    }

    bindMediaQuery() {
        // get media query from custom property
        const rootStyle = getComputedStyle(document.documentElement);

        // max nav - mobile
        const maxQuery = rootStyle.getPropertyValue('--max-nav-media-query');
        this.maxMediaQuery = window.matchMedia(maxQuery);
        // listen and set initial
        this.maxMediaQuery.addEventListener(
            'change',
            this.onMaxMediaChange.bind(this),
        );
        this.onMaxMediaChange(this.maxMediaQuery);

        // min nav - desktop
        const minQuery = rootStyle.getPropertyValue('--min-nav-media-query');
        this.minMediaQuery = window.matchMedia(minQuery);
        // listen and set initial
        this.minMediaQuery.addEventListener(
            'change',
            this.onMinMediaChange.bind(this),
        );
        this.onMinMediaChange(this.maxMediaQuery);
    }

    onMaxMediaChange(mobileMediaQuery) {
        // mobile
        if (mobileMediaQuery.matches) {
            this.isMobile = true;
            this.isDesktop = false;
        }

        this.closeMenu();
    }

    onMinMediaChange(desktopMediaQuery) {
        // desktop
        if (desktopMediaQuery.matches) {
            this.isDesktop = true;
            this.isMobile = false;
        }

        this.closeMenu();
    }

    // Force the mobile menu closed (e.g. when crossing the nav breakpoint) and
    // release the shared scroll lock so the background doesn't stay frozen.
    closeMenu() {
        this.isMenuOpen = false;
        document.documentElement.classList.remove('nav-menu-open');
        this.$menuLabel.setAttribute('aria-expanded', 'false');
        window.scrollLock?.unlock('nav');
    }

    onsearchopen() {
        this.searchIsOpen = true;
    }

    onsearchclose() {
        this.searchIsOpen = false;
        if (this.isMobile) {
            this.closeSubnav();
        }
    }

    // toggle subnav on .nav__item__button click
    onclick(event) {
        const $itemButton = event.target.closest('.nav__item__button');
        const $nestedButton = event.target.closest('[data-js-nested-button]');

        // Handle nested dropdown buttons
        if ($nestedButton) {
            this.handleNestedDropdown($nestedButton);
            return;
        }

        if (!$itemButton) return;

        const $item = $itemButton.closest('.nav__item');

        // Prevent hover from interfering with click
        this.isClicking = true;
        setTimeout(() => {
            this.isClicking = false;
        }, 100);

        // Handle click behavior separately from hover
        if ($item === this.clickOpenedSubnav) {
            // Clicking the same button that was clicked before - close it
            this.closeSubnav();
            this.clickOpenedSubnav = null;
        } else {
            // Clicking a different button or no button was clicked before - open it
            this.closeSubnav(); // Close any existing subnav
            this.clickOpenedSubnav = null; // Reset click state
            this.openSubnav($item);
            this.clickOpenedSubnav = $item;
        }
    }

    onkeydown(event) {
        if (event.key === 'Escape') {
            if (this.isMenuOpen) {
                this.closeMenu();
                this.$menuLabel.focus();
            }
            this.closeSubnav();
            return;
        }
        const isOpeningSubnav =
            OPEN_SUBNAV_KEYS.includes(event.key) &&
            event.target.matches('[data-js-level-1-button]');

        if (isOpeningSubnav) {
            event.preventDefault();
            const $item = event.target.closest('.nav__item');
            if ($item === this.$openSubnavItem && event.key !== 'ArrowDown') {
                this.closeSubnav();
            } else {
                this.openSubnav($item);
            }
        }
    }

    // open/close subnav on hover, desktop only
    onmouseover(event) {
        if (this.isMobile || this.isClicking) return;

        // Check if hovering over any nav item (button or link)
        const $navButton = event.target.closest('.nav__item__button');
        if (!$navButton) return;

        const $item = $navButton.closest('.nav__item');
        if (!$item) return;

        // Handle hover behavior
        if (this.clickOpenedSubnav) {
            // If there's a click-opened subnav, close it when hovering over a different item
            if ($item !== this.clickOpenedSubnav) {
                this.closeSubnav();
                this.clickOpenedSubnav = null;
                // Only open subnav if this item has one
                this.openSubnav($item);
            }
        } else {
            // If hovering over a different item, close current and open new one (if it has subnav)
            if ($item !== this.$openSubnavItem) {
                this.closeSubnav();
                this.openSubnav($item);
            }
        }
    }

    // close subnav when mouse leaves the navigation area, desktop only
    onmouseleave(event) {
        if (this.isMobile) return;

        // Only close if we're leaving the entire navigation element
        // Don't close if we're just moving between nav items
        if (
            !event.relatedTarget ||
            !this.$element.contains(event.relatedTarget)
        ) {
            // Only close hover-opened subnavs, not click-opened ones
            if (!this.clickOpenedSubnav) {
                this.closeSubnav();
            }
        }
    }

    // handle clicks outside the navigation to close click-opened subnavs
    onDocumentClick(event) {
        if (this.clickOpenedSubnav && !this.$element.contains(event.target)) {
            this.closeSubnav();
            this.clickOpenedSubnav = null;
        }
    }

    // programmatically trigger click to avoid browser setting scroll position to top
    onMenuLabelClick(event) {
        event.preventDefault();

        if (this.searchIsOpen) {
            const customEvent = new Event('menulabelclicked');
            window.dispatchEvent(customEvent);
        }

        this.isMenuOpen = !this.isMenuOpen;
        document.documentElement.classList.toggle(
            'nav-menu-open',
            this.isMenuOpen,
        );
        // Shared background scroll lock (also used by the sign-up panel).
        if (this.isMenuOpen) {
            window.scrollLock?.lock('nav');
        } else {
            window.scrollLock?.unlock('nav');
        }
        this.$menuLabel.setAttribute(
            'aria-expanded',
            this.isMenuOpen.toString(),
        );
    }

    openSubnav($item) {
        if (!$item) return;

        this.closeSubnav(this.$openSubnavItem); // close previous

        this.$openSubnavItem = $item;
        this.$openSubnavButton = $item.querySelector(
            '[data-js-level-1-button]',
        );

        // Only proceed if this is actually a subnav button
        if (!this.$openSubnavButton) return;

        this.$openSubnavItem.classList.add('is-subnav-open');
        this.$openSubnavButton.setAttribute('aria-expanded', 'true');
    }

    closeSubnav() {
        if (!this.$openSubnavItem) return;

        this.$openSubnavItem.classList.remove('is-subnav-open');
        if (this.$openSubnavButton) {
            this.$openSubnavButton.setAttribute('aria-expanded', 'false');
        }
        this.$openSubnavItem = null;
        this.$openSubnavButton = null;
    }

    toggleSubnav($item) {
        if ($item === this.$openSubnavItem) {
            this.closeSubnav();
        } else {
            this.openSubnav($item);
        }
    }

    handleNestedDropdown($nestedButton) {
        const $nestedList = $nestedButton.nextElementSibling;
        const isExpanded =
            $nestedButton.getAttribute('aria-expanded') === 'true';

        // Close all other nested dropdowns in the same subnav
        const $subnav = $nestedButton.closest('.nav__subnav');
        const $otherNestedButtons = $subnav.querySelectorAll(
            '[data-js-nested-button]',
        );
        $otherNestedButtons.forEach((button) => {
            if (button !== $nestedButton) {
                button.setAttribute('aria-expanded', 'false');
                button.nextElementSibling.setAttribute('aria-hidden', 'true');
            }
        });

        // Toggle current nested dropdown
        if (isExpanded) {
            $nestedButton.setAttribute('aria-expanded', 'false');
            $nestedList.setAttribute('aria-hidden', 'true');
        } else {
            $nestedButton.setAttribute('aria-expanded', 'true');
            $nestedList.setAttribute('aria-hidden', 'false');
        }
    }

    unmount() {
        window.removeEventListener('resize', this.onScroll);
        this.$element.removeEventListener('keydown', this);
        this.mediaQuery.removeEventListener('change', this.onMediaChange);
        this.$element.removeEventListener('mouseover', this);
        this.$element.removeEventListener('mouseleave', this);
        document.removeEventListener('click', this.onDocumentClick.bind(this));
        window.removeEventListener('scroll', this.onScroll);
    }
}

registerModule('Nav', Nav);
